/**
 * Modale "Ajouter une note".
 */
import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import {
  X, StickyNote, Paperclip, Bold, Italic, Underline, List, Link2, FileText,
} from 'lucide-react';
import { useClientActions } from './ClientActionsContext';
import { useToast } from '../../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VISIBILITIES = [
  { id: 'cs', label: 'Équipe Customer Success' },
  { id: 'commercial', label: 'Commercial uniquement' },
  { id: 'leadership', label: 'Direction uniquement' },
];

export default function NoteModal() {
  const { modal, client, close } = useClientActions();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const isOpen = modal === 'note' && !!client;

  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState('cs');
  const [attachment, setAttachment] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (f) setAttachment(f);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/customer-success/actions`, {
        companyName: client.companyName,
        clientEmail: client.primaryEmail,
        action: 'note',
        body,
        visibility,
        metadata: attachment ? { attachmentName: attachment.name } : undefined,
      });
      toast({ title: 'Note enregistrée', description: `Note ajoutée pour ${client.companyName}.` });
      setBody(''); setAttachment(null);
      close();
    } catch (e) {
      toast({
        title: 'Erreur',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden flex flex-col" data-testid="note-modal">
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <StickyNote className="w-4 h-4 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Ajouter une note</h2>
              <p className="text-xs text-gray-500">Enregistrer une information sur {client.companyName}</p>
            </div>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600" data-testid="close-note-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pb-4 space-y-4 overflow-auto">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Note</label>
            <div className="border border-b-0 border-gray-200 rounded-t-md px-2 py-1 flex items-center gap-2 bg-gray-50">
              {[Bold, Italic, Underline, List, Link2].map((Ic, i) => (
                <button key={i} tabIndex={-1} className="p-1 rounded hover:bg-gray-200 text-gray-500">
                  <Ic className="w-3.5 h-3.5" />
                </button>
              ))}
            </div>
            <Textarea
              rows={7}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Saisissez votre note ici..."
              className="rounded-t-none font-mono text-xs"
              data-testid="note-body"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Visibilité</label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="note-visibility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VISIBILITIES.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Pièces jointes (optionnel)</label>
            <input ref={fileRef} type="file" hidden onChange={handlePick} data-testid="note-file-input" />
            {attachment ? (
              <div className="flex items-center gap-2 text-xs bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                <FileText className="w-3.5 h-3.5 text-gray-500" />
                <span className="flex-1 truncate">{attachment.name}</span>
                <button onClick={() => setAttachment(null)} className="text-red-500 hover:text-red-600 text-[10px]">Retirer</button>
              </div>
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1.5 border border-dashed border-gray-200 rounded-md px-3 py-2 w-full justify-center"
                data-testid="note-attach-btn"
              >
                <Paperclip className="w-3.5 h-3.5" /> Joindre un fichier
              </button>
            )}
            <p className="text-[10px] text-gray-400 mt-1">Formats acceptés : PDF, JPG, PNG (max. 10 Mo)</p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={close} data-testid="note-cancel-btn">Annuler</Button>
          <Button
            size="sm" onClick={handleSave}
            disabled={!body.trim() || saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="note-save-btn"
          >
            <StickyNote className="w-3.5 h-3.5 mr-1.5" />
            {saving ? 'Enregistrement...' : 'Enregistrer la note'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
